import { Box, Stack } from '@mui/material'
import {
    ArrowUpRightIcon,
    BuildingOfficeIcon,
    CalendarDotIcon,
    DownloadSimpleIcon,
    ExportIcon,
    FilePdfIcon,
    FilesIcon,
} from '@phosphor-icons/react'
import {
    AppraiserAssignment,
    assembleAddressFormat,
    Breadcrumbs,
    CardItem,
    DeviationAnalysisWidget,
    exportDeviationAnalysisToExcel,
    Footer,
    getDeviationAnalysisExportCopy,
    getDeviationAnalysisWidgetCopy,
    isNotNullOrUndefined,
    isoDateStringToLocalizedMonthYear,
    isoDateStringToLocalFormat,
    LoadingSkeleton,
    mapDeviationAnalysisSchemaToTable,
    MultiStructuresInfoBox,
    PageGridContainer,
    PageGridItem,
    TinyButton,
    TinyNavButton,
    TitleImage,
    tokens,
    UserLinkList,
    ValueCheckBaseValuationWidget,
    ValueCheckImageWidget,
    ValueCheckMarketDataLandWidget,
    ValueCheckMarketDataWidget,
    ValueCheckOverviewMetricGrid,
    ValueCheckSensitivityMatrixWidget,
    ValueCheckTenantSummaryWidget,
} from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { Headline } from 'ComponentLibrary/src/piazza/text/Headline/Headline'
import { generatePath, useLocation, useNavigate } from 'react-router-dom'
import { useValueCheckPageLayout } from './ValueCheckPageLayout.data'
import ValueCheckPageLayoutLoading from './ValueCheckPageLayout.load'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'
import { getLatestUrlParams } from 'ComponentLibrary/src/hooks/useUrlState'
import { isEmpty, noop } from 'lodash'
import { DYNAMIC_APP_PATHS } from 'ClientPortal/src/router/constants'
import { getValuationStructuresWithIndex } from 'ClientPortal/src/pages/Assets/AssetDetails/AssetDetails.utils'
import { ExportActionButton } from 'ComponentLibrary/src/piazza/buttons/ExportActionButton/ExportActionButton'
import { useTranslation } from 'react-i18next'
import { getMultiStructuresInfoBoxCopy } from 'ClientPortal/src/pages/Assets/AssetDetails/multiStructuresInfoBoxCopy'
import { getValueCheckLandMarketDataWidgetCopy, getValueCheckMarketDataWidgetCopy } from './valueCheckMarketDataCopy'
import {
    getValueCheckBaseValuationWidgetCopy,
    getValueCheckOverviewMetricGridCopy,
    getValueCheckSensitivityMatrixWidgetCopy,
    getValueCheckTenantSummaryWidgetCopy,
} from './valueCheckWidgetCopy'
import { useUserLinkCopy } from 'ClientPortal/src/i18n/useUserLinkCopy'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

export default function ValueCheckPageLayout() {
    const { t: tCommon } = useTranslation('terminology', { keyPrefix: 'common' })
    const { t: tActions } = useTranslation('terminology', { keyPrefix: 'common.actions' })
    const { t: tDeviationAnalysisWidget } = useTranslation('terminology', { keyPrefix: 'common.deviationAnalysisWidget' })
    const { t: tExport } = useTranslation('terminology', { keyPrefix: 'export' })
    const { t: tTenantBreakdownTable } = useTranslation('terminology', { keyPrefix: 'common.tenantBreakdownTable' })
    const { t: tValueCheckPageLayout } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.valueCheck.valueCheckPageLayout' })
    const { t: tValueCheckOverviewMetricGrid } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valueCheck.valueCheckPageLayout.overviewMetricGrid',
    })
    const { t: tValueCheckBaseValuationWidget } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valueCheck.valueCheckPageLayout.baseValuationWidget',
    })
    const { t: tValueCheckTenantSummaryWidget } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valueCheck.valueCheckPageLayout.tenantSummaryWidget',
    })
    const { t: tValueCheckMarketDataWidget } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valueCheck.valueCheckPageLayout.marketDataWidget',
    })
    const { t: tValueCheckLandMarketDataWidget } = useTranslation('pages', {
        keyPrefix: 'assets.assetDetails.valueCheck.valueCheckPageLayout.landMarketDataWidget',
    })
    const userLinkCopy = useUserLinkCopy()
    const locale = useCurrentResolvedLocale()
    const {
        valueCheck,
        baseValuation,
        asset,
        order,
        assetId,
        appraiserUsers,
        isValueCheckPageLayoutPending,
        assignedCustomerUsers,
        isOrderInitialLoading,
        isCustomerUsersPending,
        baseValuationAppraiserUsers,
        downloadReport,
        isDownloadingReport,
        valuationOptions,
        baseValuationOption,
        comparisonValuationOption,
        deviationAnalysis,
        isDeviationAnalysisPending,
        assetTitle,
    } = useValueCheckPageLayout()

    const { pathname } = useLocation()
    const navigate = useNavigate()
    const deviationAnalysisExportCopy = getDeviationAnalysisExportCopy(tCommon, tExport)
    const deviationAnalysisWidgetCopy = getDeviationAnalysisWidgetCopy(
        deviationAnalysisExportCopy,
        tDeviationAnalysisWidget,
        comparisonValuationOption?.valuationDate ?? comparisonValuationOption?.date
    )
    const multiStructuresInfoBoxCopy = getMultiStructuresInfoBoxCopy(tCommon)
    const valueCheckOverviewMetricGridCopy = getValueCheckOverviewMetricGridCopy(tCommon, tValueCheckOverviewMetricGrid)
    const valueCheckBaseValuationWidgetCopy = getValueCheckBaseValuationWidgetCopy(tCommon, tActions, tValueCheckBaseValuationWidget)
    const valueCheckTenantSummaryWidgetCopy = getValueCheckTenantSummaryWidgetCopy(
        tCommon,
        tTenantBreakdownTable,
        tValueCheckTenantSummaryWidget
    )
    const valueCheckSensitivityMatrixWidgetCopy = getValueCheckSensitivityMatrixWidgetCopy(tCommon)
    const valueCheckMarketDataWidgetCopy = getValueCheckMarketDataWidgetCopy(tCommon, tValueCheckMarketDataWidget)
    const valueCheckLandMarketDataWidgetCopy = getValueCheckLandMarketDataWidgetCopy(tCommon, tValueCheckLandMarketDataWidget)

    // When we can't find a value check, redirect to the asset overview page.
    if (!isValueCheckPageLayoutPending && asset && !valueCheck) {
        void navigate(`/assets/${assetId}/valuations`)
        return null
    }

    const { street, house_number } = asset?.address ?? {}
    const addressLine = assembleAddressFormat({ street, house_number }, { delimiter: ', ' })
    const valueCheckDate = isoDateStringToLocalFormat(valueCheck?.valuation_date, false, locale)
    const valueCheckMonthAndYear = isoDateStringToLocalizedMonthYear(valueCheck?.valuation_date, 'numeric', locale)
    const valueCheckTitle = `${tCommon('valueCheck')} ${valueCheckMonthAndYear}`
    const customerAssetReference = asset?.customer_asset_reference ? `[${asset?.customer_asset_reference}]` : undefined
    const appraisingEntityName = baseValuation?.valuation_type === 'external' ? baseValuation.appraising_entity_name : undefined
    const baseValuationUrl =
        baseValuation && assetId
            ? `/${generatePath(DYNAMIC_APP_PATHS.valuationDetailsPage, {
                  assetId,
                  valuationId: baseValuation.uuid,
              })}`
            : undefined

    const breadCrumbItems = [
        { title: tCommon('allAssets'), link: '/assets', params: getLatestUrlParams('client-asset-filter') },
        { title: tCommon('asset'), link: `/assets/${assetId}/overview` },
        { title: tCommon('valueCheck') },
    ]
    const breadcrumbs = <Breadcrumbs items={breadCrumbItems} currentLocation={pathname} />

    if (isValueCheckPageLayoutPending || !valueCheck || !baseValuation) {
        return <ValueCheckPageLayoutLoading breadcrumbs={breadcrumbs} />
    }

    const titleImageSrc = asset?.title_image_thumbnail ?? asset?.title_image
    const structures = getValuationStructuresWithIndex(asset, baseValuation)
    const transformedDeviationAnalysis = mapDeviationAnalysisSchemaToTable(deviationAnalysis)
    const baseTenantSummaryItems = baseValuation.tenant_summary ? Object.values(baseValuation.tenant_summary) : undefined
    const valueCheckTenantSummaryItems = valueCheck.tenant_summary_items

    return (
        <Stack direction="column" minHeight="100%" bgcolor={tokens.ColorBackgroundDark}>
            <Stack direction="column" gap={tokens.SizingSpacing300}>
                <PageGridContainer background={`linear-gradient(${tokens.ColorBackgroundLight} 235.5px, transparent 235.5px)`}>
                    <PageGridItem columnSpan={6} columnSpanXs={4}>
                        <Stack direction="column" gap={tokens.SizingSpacing400}>
                            <Stack
                                direction="row"
                                paddingTop={tokens.SizingSpacing200}
                                paddingBottom={tokens.SizingSpacing300}
                                gap={tokens.SizingSpacing300}
                                width="100%"
                                alignItems="flex-end"
                            >
                                <Stack direction="column" flexGrow={1} gap={tokens.SizingSpacing300}>
                                    {breadcrumbs}
                                    <Stack direction="row" gap={tokens.SizingSpacing400} alignItems="flex-end">
                                        <Stack direction="column" gap={tokens.SizingSpacing300}>
                                            <Stack direction="column">
                                                <Typography variant="textM" color={tokens.ColorTextSubtle}>
                                                    {tValueCheckPageLayout('sectionLabel')}
                                                </Typography>
                                                <Stack direction="column" gap={tokens.SizingSpacing100}>
                                                    <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                                        <Headline variant="headline1" title={valueCheckTitle} fullWidth={false} />
                                                    </Stack>
                                                    <Stack
                                                        direction="row"
                                                        gap={tokens.SizingSpacing100}
                                                        maxWidth="max(460px, calc(100vw - 520px))"
                                                    >
                                                        <TextWithEllipsis
                                                            tooltipText={`${addressLine} ${customerAssetReference}`}
                                                            typographyProps={{ variant: 'headline2', color: tokens.ColorTextBoldest }}
                                                        >
                                                            {addressLine}
                                                            {customerAssetReference && (
                                                                <Box
                                                                    display="inline-block"
                                                                    marginLeft={tokens.SizingSpacing100}
                                                                    sx={{ verticalAlign: 'bottom' }}
                                                                >
                                                                    <Typography variant="headline3" color={tokens.ColorTextDefault}>
                                                                        {customerAssetReference}
                                                                    </Typography>
                                                                </Box>
                                                            )}
                                                        </TextWithEllipsis>
                                                    </Stack>
                                                </Stack>
                                            </Stack>
                                            <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center" height="32px">
                                                {isOrderInitialLoading || isCustomerUsersPending ? (
                                                    <LoadingSkeleton variant="rounded" height="32px" width="500px" />
                                                ) : (
                                                    <>
                                                        <Stack direction="row" gap={tokens.SizingSpacing100} alignItems="center">
                                                            <CalendarDotIcon color={tokens.ColorIconBoldest} size={tokens.SizingIconM} />
                                                            <Typography variant="textS" color={tokens.ColorTextBold} noWrap>
                                                                {valueCheckDate}
                                                            </Typography>
                                                        </Stack>
                                                        {valueCheck?.appraiser_assignment && (
                                                            <AppraiserAssignment
                                                                appraiserAssignment={valueCheck.appraiser_assignment}
                                                                users={appraiserUsers}
                                                            />
                                                        )}
                                                        <UserLinkList users={assignedCustomerUsers} copy={userLinkCopy} />
                                                    </>
                                                )}
                                            </Stack>
                                        </Stack>
                                        <Stack
                                            direction="column"
                                            minHeight="100%"
                                            gap={tokens.SizingSpacing800}
                                            width="100%"
                                            alignItems="flex-end"
                                        >
                                            {valueCheck.report_file && (
                                                <Stack direction="row" gap={tokens.SizingSpacing150} alignItems="center">
                                                    <Typography variant="textS" color={tokens.ColorTextDefault}>
                                                        {tExport('sectionTitle')}
                                                    </Typography>
                                                    <ExportActionButton
                                                        leftIcon={<FilePdfIcon />}
                                                        rightIcon={<DownloadSimpleIcon />}
                                                        label={tCommon('valueCheck')}
                                                        onClick={() => downloadReport({ valueCheckId: valueCheck.uuid })}
                                                        disabled={isDownloadingReport}
                                                    />
                                                </Stack>
                                            )}
                                            {isOrderInitialLoading ? (
                                                <Stack direction="column" alignItems="flex-end" flexGrow={1} gap={tokens.SizingSpacing200}>
                                                    <LoadingSkeleton variant="rounded" height="24px" width="150px" />
                                                    <LoadingSkeleton variant="rounded" height="24px" width="150px" />
                                                </Stack>
                                            ) : (
                                                <Stack direction="column" alignItems="flex-end" flexGrow={1}>
                                                    <TinyNavButton
                                                        label={tActions('viewAsset')}
                                                        leftIcon={<BuildingOfficeIcon />}
                                                        rightIcon={<ArrowUpRightIcon />}
                                                        href={`/assets/${assetId}`}
                                                    />
                                                    {order?.status !== 'temp_hide_in_portal' && valueCheck?.order_uuid && (
                                                        <TinyNavButton
                                                            label={tActions('viewOrder')}
                                                            leftIcon={<FilesIcon />}
                                                            rightIcon={<ArrowUpRightIcon />}
                                                            href={`/orders/${valueCheck.order_uuid}/overview`}
                                                        />
                                                    )}
                                                </Stack>
                                            )}
                                        </Stack>
                                    </Stack>
                                </Stack>

                                <TitleImage
                                    width={255}
                                    height={170}
                                    image={titleImageSrc ? { src: titleImageSrc, alt: tCommon('assetImageAlt') } : undefined}
                                    fallbackImageSrc={[asset?.title_image].filter(isNotNullOrUndefined)}
                                />
                            </Stack>
                        </Stack>
                    </PageGridItem>
                </PageGridContainer>
                <PageGridContainer>
                    <PageGridItem columnSpan={4} columnSpanXs={4}>
                        <CardItem height="100%">
                            <ValueCheckOverviewMetricGrid
                                valueCheck={valueCheck}
                                baseValuationMortgageLendingValue={baseValuation.mortgage_lending_value}
                                copy={valueCheckOverviewMetricGridCopy}
                                locale={locale}
                            />
                        </CardItem>
                    </PageGridItem>
                    <PageGridItem columnSpan={2} columnSpanXs={4}>
                        <CardItem>
                            <ValueCheckBaseValuationWidget
                                valuationDate={baseValuation.valuation_date}
                                valuationType={baseValuation.valuation_type}
                                appraiserAssignment={baseValuation.appraiser_assignment}
                                appraisingEntityName={appraisingEntityName}
                                marketValue={baseValuation.market_value}
                                mortgageLendingValue={baseValuation.mortgage_lending_value}
                                baseValuationAppraiserUsers={baseValuationAppraiserUsers}
                                baseValuationUrl={baseValuationUrl}
                                copy={valueCheckBaseValuationWidgetCopy}
                                locale={locale}
                            />
                        </CardItem>
                    </PageGridItem>
                    <PageGridItem columnSpan={6} columnSpanXs={4}>
                        <CardItem>
                            <ValueCheckTenantSummaryWidget
                                baseTenantSummary={baseTenantSummaryItems ?? []}
                                valueCheckTenantSummary={valueCheckTenantSummaryItems ?? []}
                                baseValuationDate={baseValuation.valuation_date}
                                valueCheckDate={valueCheck.valuation_date}
                                copy={valueCheckTenantSummaryWidgetCopy}
                                locale={locale}
                            />
                        </CardItem>
                    </PageGridItem>
                    <PageGridItem columnSpan={isEmpty(structures) ? 6 : 4} columnSpanXs={4}>
                        <CardItem>
                            <Stack direction="column" gap={tokens.SizingSpacing200}>
                                <Stack direction="row" gap={tokens.SizingSpacing200} justifyContent="space-between">
                                    <Headline title={tExport('deviationAnalysis.deviationAnalysisTitle')} variant="headline3" />
                                    <TinyButton
                                        leftIcon={<ExportIcon />}
                                        label={tExport('deviationAnalysis.deviationAnalysisActionButtonLabel')}
                                        onClick={() =>
                                            exportDeviationAnalysisToExcel({
                                                deviationAnalysis: transformedDeviationAnalysis,
                                                assetTitle,
                                                baseValuation: baseValuationOption,
                                                comparisonValuation: comparisonValuationOption,
                                                copy: deviationAnalysisExportCopy,
                                                locale,
                                            })
                                        }
                                    />
                                </Stack>{' '}
                                <DeviationAnalysisWidget
                                    deviationAnalysis={deviationAnalysis}
                                    isDeviationAnalysisPending={isDeviationAnalysisPending}
                                    valuationOptions={valuationOptions}
                                    setBaseValuationId={noop}
                                    setComparisonValuationId={noop}
                                    baseValuation={baseValuationOption}
                                    comparisonValuation={comparisonValuationOption}
                                    copy={deviationAnalysisWidgetCopy}
                                    locale={locale}
                                    lockBaseValuation
                                    lockBaseValuationTitle={baseValuationOption?.title}
                                    lockComparisonValuation
                                />
                            </Stack>
                        </CardItem>
                    </PageGridItem>
                    {!isEmpty(structures) && (
                        <PageGridItem columnSpan={2} columnSpanXs={2}>
                            <Stack gap={tokens.SizingSpacing300} position="sticky" top={tokens.SizingSpacing200} height="fit-content">
                                <MultiStructuresInfoBox
                                    structures={structures}
                                    copy={multiStructuresInfoBoxCopy}
                                    hideFictionalConstructionYear
                                />
                            </Stack>
                        </PageGridItem>
                    )}
                    {valueCheck.sensitivity_matrix && (
                        <PageGridItem columnSpan={6} columnSpanXs={4}>
                            <CardItem>
                                <ValueCheckSensitivityMatrixWidget
                                    sensitivityMatrix={valueCheck.sensitivity_matrix}
                                    copy={valueCheckSensitivityMatrixWidgetCopy}
                                    locale={locale}
                                />
                            </CardItem>
                        </PageGridItem>
                    )}
                    <PageGridItem columnSpan={3} columnSpanXs={4}>
                        <CardItem height="100%">
                            <ValueCheckMarketDataWidget
                                marketData={valueCheck.market_data}
                                copy={valueCheckMarketDataWidgetCopy}
                                locale={locale}
                            />
                        </CardItem>
                    </PageGridItem>
                    {valueCheck.rental_development_image && (
                        <PageGridItem columnSpan={3} columnSpanXs={4}>
                            <CardItem height="100%">
                                <ValueCheckImageWidget caption={'RIWIS'} imageSrc={valueCheck.rental_development_image.url} />
                            </CardItem>
                        </PageGridItem>
                    )}
                    {valueCheck.market_data?.land && (
                        <PageGridItem columnSpan={6} columnSpanXs={4}>
                            <CardItem>
                                <ValueCheckMarketDataLandWidget
                                    landMarketData={valueCheck.market_data.land}
                                    copy={valueCheckLandMarketDataWidgetCopy}
                                    locale={locale}
                                />
                            </CardItem>
                        </PageGridItem>
                    )}
                </PageGridContainer>
            </Stack>
            <Footer withTopSpace />
        </Stack>
    )
}
